import { Box, Button, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useNavigate, useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import PinnacleAutocomplete from "../../components/PinnacleAutocomplete/PinnacleAutocomplete";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import { SELECT_VIDEO_PLAYER_FULL_BG } from "../../utils/constants";
import { useCallback, useEffect, useState } from "react";
import { Video } from "../../api/interfaces/Video";
import { getVideoById } from "../../api/services/video";

interface PlayerOptions {
    label: string;
    name: string;
}
export default function SelectVideoPlayerPage() {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');
    const { videoId } = useParams();

    const [video, setVideo] = useState<Video | null>(null);

    const fetchVideo = useCallback(async () => {
        try {
            if (!videoId) throw new Error('Video ID is missing');
            const response = await getVideoById(videoId);
            setVideo(response.data.video);
        } catch (error) {
            console.error(error);
        }
    }, [videoId]);

    const playerOptions = [] as PlayerOptions[];
    if (video?.videoUrl?.trim()) {
        playerOptions.push({ label: 'rumble', name: 'Player 1' });
    }
    if (video?.videoUrl1?.trim()) {
        playerOptions.push({ label: 'odysee', name: 'Player 2' });
    }
    if (video?.videoUrl2?.trim()) {
        playerOptions.push({ label: 'vimeo', name: 'Player 3' });
    }
    // const playerOptions = [
    //     { label: 'rumble', name: 'Player 1 (Always use Player 1 by default)' },
    //     { label: 'odysee', name: 'Player 2' },
    //     { label: 'vimeo', name: 'Player 3 (Important: Only use Player 3 if Player 1 or Player 2 is not working)' },
    // ] as PlayerOptions[];

    const handleSelectVideoPlayer = (
        _: React.SyntheticEvent,
        newValue: PlayerOptions | null
    ) => {
        if (newValue?.label === 'rumble') {
            navigate(`/watch/player1/${videoId}`);
        } else if (newValue?.label === 'odysee') {
            navigate(`/watch/player2/${videoId}`);
        } 
        else if (newValue?.label === 'vimeo') {
            navigate(`/watch/player3/${videoId}`);
        }
    }

    useEffect(() => {
        const init = async () => {
            await fetchVideo();
        };
        init();
    }, [fetchVideo]);

    return (
        <main style={{
            display: 'flex',
            width: '100%',
            minHeight: '100vh',
            backgroundImage: `url(${SELECT_VIDEO_PLAYER_FULL_BG})`,
            backgroundSize: 'cover',
            justifyContent: 'center'
        }}>
            {isMobile && (
                <IconButton
                    aria-label="Close"
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: 'white',
                        zIndex: 9999,
                    }}
                    onClick={() => navigate(-1)}
                >
                    <CloseIcon />
                </IconButton>
            )}

            <Box
                sx={{
                    flex: 1,
                    color: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    p: 3,
                    maxWidth: '560px',
                    zIndex: 2,
                    position: 'relative'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                        height: 50,
                        position: 'relative',
                    }}
                >
                    {/* Back button */}
                    <Button
                        variant="contained"
                        sx={{
                            background: 'transparent',
                            color: 'white',
                            cursor: 'pointer',
                            minWidth: 'auto',
                            padding: 0,
                            zIndex: 1,
                        }}
                        aria-label="Back"
                        onClick={() => navigate(-1)}
                    >
                        <KeyboardBackspaceOutlinedIcon />
                    </Button>

                    {/* Title */}
                    <Typography
                        variant="h5"
                        fontWeight={600}
                        noWrap
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                            color: 'white',
                        }}
                    >
                        Select Video Player
                    </Typography>

                    {/* Invisible Box for symmetry */}
                    <Box sx={{ width: 40 }} />
                </Box>


                <PinnacleAutocomplete
                    label="Select"
                    placeholder="Select..."
                    options={playerOptions}
                    getOptionLabel={(option) => option.name ?? ''}
                    fullWidth
                    onChange={(_, newValue) => handleSelectVideoPlayer(_, newValue)}
                    renderInput={(params) => (
                        <PinnacleTextField
                            {...params}
                            variant="outlined"
                            sx={{
                                '& .MuiSvgIcon-root': { color: '#fff' },
                                '& .MuiInputBase-root': { color: '#fff' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A8A8' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A8A8' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#A8A8A8' },
                            }}
                        />
                    )}
                    slotProps={{
                        paper: {
                            sx: {
                                backgroundColor: '#222',
                                color: '#fff',
                                '& .MuiAutocomplete-option': {
                                    backgroundColor: '#222',
                                    '&:hover': { backgroundColor: '#333' },
                                    '&.Mui-focused': { backgroundColor: '#333' },
                                    '&[aria-selected="true"]': {
                                        backgroundColor: '#007BFF !important',
                                        color: '#fff',
                                    },
                                },
                            },
                        },
                    }}
                />

                {/* Video Card */}
                <Box sx={{ mt: 6 }}>
                    <img
                        src={video?.thumbnail}
                        alt={video?.title}
                        style={{ borderRadius: 5, width: '100%', display: 'block' }}
                    />
                    <Box sx={{ mt: 2 }}>
                        <Typography fontWeight={700}>
                            {video?.title}
                        </Typography>
                        <Typography variant="subtitle2">
                            {video?.duration}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            {video?.subjectName}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </main>
    );
}
